<template>
  <div class="pulse-survey">
    <div class="section-header">
      <div class="align-center">
        <i class="material-icons clickable" @click="$router.push('/pulse-surveys')">arrow_back</i>
        <span>Pulse Survey</span>
      </div>
      <span class="row">
        <h-btn @click="$router.push({name: 'pulseSurveyPreview', params: {id: $route.params.id}})">Preview</h-btn>
        <h-btn @click="save" v-if="pulseSurvey.editable">Save</h-btn>
      </span>
    </div>
    <div class="card card--white">
      <div class="card--container">
      <v-alert type="info" :value="!pulseSurvey.editable" class="small-bottom">This survey is managed by Habit Technologies LLC and is not editable</v-alert>
      <div>
        <h-input v-model="pulseSurvey.name" :readonly="!pulseSurvey.editable" label="Pulse Survey Name"/>
        <h-textarea label="Open ended question" :disabled="!pulseSurvey.editable" type="text" v-model="pulseSurvey.open_ended_question" />
        <h-input
          type="number"
          label="How many randomly selected questions should be asked per day?"
          messages="Questions that are chosen at random are not asked every day. One or more of these questions is chosen each day from a pool of questions. This helps prevent response fatigue."
          v-model="pulseSurvey.daily_num_questions"
          :readonly="!pulseSurvey.editable"/>
        <div class="small-top label">Number of questions that are marked "always asked" (e.g., they are asked every day)</div>
        <div>{{alwaysBeingAsked}}</div>
        <div class="label">Total number of questions asked each day</div>
        <div>{{ Number(pulseSurvey.daily_num_questions) + alwaysBeingAsked }}</div>
        <v-alert type="error" :value="error">{{error}}</v-alert>
        <div class="section-header">
          <div class="section-headers">Graphs</div>
          <add-graph @created="(d) => dimensions.push(d)" :pulseSurvey="$route.params.id" v-if="pulseSurvey.editable"/>
        </div>
        <div v-for="dimension in dimensions" :key="dimension.id">
          <graph :graph="dimension" @deleted="removeDimension" />
        </div>
        <!-- <dimension :dimension="dimension" v-for="dimension in dimensions" :key="dimension.id" /> -->
      </div>
        <div>
          <!-- <construct :construct="construct" v-for="construct in constructs" :key="construct.id" :dimensions="dimensions" /> -->
          <div class="section-header">
            <div class="section-headers">Questions</div>
            <add-question
              :dimensions="dimensions"
              :pulseSurvey="$route.params.id"
              v-if="pulseSurvey.editable"
              @created="(q) => questions.push(q)"/>
          </div>
          <question
            v-for="question in questions"
            :key="question.id"
            :question="question"
            :dimensions="dimensions"
            :editable="!!pulseSurvey.editable" 
            @deleted="removeQuestion" />
        </div>
      </div>
    </div>
    <div class="card card--white small-top">
      <div class="card--container">
        <client-list :clients="clients" @revoke="revoke" @addClients="addClients" header="Clients Assigned To This Survey"/>
      </div>
    </div>
  </div>
</template>

<script>
import dataStore from "@/services/api/store";
import snackbarHub from "@/services/hubs/snackbar";
import dimension from "./dimension";
import question from "./question";
import addQuestion from "./addQuestion";
import addGraph from "./addGraph";
import graph from "./graph";
import clientList from "@/pages/coaches/components/clients/list";

const CLIENT_STORE = "CoachClients";
const CLIENT_PROFILE_STORE = "CoachClientProfile";
const PULSE_SURVEY_STORE = "CoachPulseSurveys";
const PULSE_SURVEY_CONSTRUCT_STORE = "CoachPulseSurveyConstruct";
const PULSE_SURVEY_DIMINSION_STORE = "CoachPulseSurveyDiminsion";
const PULSE_SURVEY_QUESTION_STORE = "CoachPulseSurveyQuestions";

export default {
  data() {
    return {
      pulseSurvey: {},
      dimensions: [],
      constructs: [],
      questions: [],
      selectedQuestion: {},
      newQuestion: {},
      error: "",
      clients: []
    };
  },
  components: {
    dimension,
    question,
    addQuestion,
    addGraph,
    graph,
    clientList
  },
  created() {
    this.pulseSurveyId = this.$route.params.id;
    dataStore
      .find(PULSE_SURVEY_STORE, this.pulseSurveyId)
      .then(pulseSurvey => (this.pulseSurvey = pulseSurvey));

    dataStore
      .findAll(PULSE_SURVEY_CONSTRUCT_STORE, {
        pulse_survey: this.pulseSurveyId
      })
      .then(constructs => (this.constructs = constructs));

    dataStore
      .findAll(
        PULSE_SURVEY_QUESTION_STORE,
        { construct__pulse_survey: this.pulseSurveyId },
        { force: true }
      )
      .then(questions => (this.questions = questions));

    dataStore
      .findAll(PULSE_SURVEY_DIMINSION_STORE, {
        pulse_survey: this.pulseSurveyId
      })
      .then(dimensions => (this.dimensions = dimensions));

    dataStore
      .findAll(
        CLIENT_STORE,
        { client_profile__assigned_pulse_survey: this.pulseSurveyId },
        { force: true }
      )
      .then(clients => (this.clients = clients));
  },
  computed: {
    alwaysBeingAsked() {
      return this.questions
        .map(q => (q.always ? 1 : 0))
        .reduce((total, c) => total + c, 0);
    }
  },
  methods: {
    removeQuestion(questionId) {
      this.questions = this.questions.filter(q => q.id !== questionId);
    },
    editQuestion(question) {
      this.selectedQuestion = question;
    },
    save() {
      this.error = "";
      this.pulseSurvey
        .save()
        .then(() => {
          snackbarHub.update("Saved");
        })
        .catch(({ response }) => {
          if (response.status < 500) {
            this.error =
              "You do not have the correct rights to update this Pulse Survey";
          } else {
            this.error =
              "There was a server. We have notified our staff. Please try again later";
          }
        });
    },
    revoke(client) {
      snackbarHub.update("Saving");
      dataStore
        .update(
          CLIENT_PROFILE_STORE,
          client.client_profile,
          { assigned_pulse_survey: null },
          { method: "PATCH" }
        )
        .then(() => {
          this.clients = this.clients.filter(c => c.id !== client.id);
          snackbarHub.update("Saved");
        });
    },
    addClients(clients) {
      snackbarHub.update("Saving");
      Promise.all(
        clients.map(client => {
          return dataStore.update(
            CLIENT_PROFILE_STORE,
            client.client_profile,
            { assigned_pulse_survey: this.$route.params.id },
            { method: "PATCH" }
          );
        })
      )
        .then(() => {
          return dataStore.findAll(
            "CoachClients",
            { client_profile__assigned_pulse_survey: this.pulseSurveyId },
            { force: true }
          );
        })
        .then(clients => (this.clients = clients))
        .then(() => {
          snackbarHub.update("Saved");
        });
    },
    destroy() {
      this.error = "";
      this.pulseSurvey
        .destroy()
        .then(() => {
          snackbarHub.update("Deleted");
          this.$router.push("/pulse-surveys");
        })
        .catch(({ response }) => {
          if (response.status === 400) {
            this.error =
              "You have Habit Programs using this Pulse Survey. Please update your Habit Programs to use a different survey before removing this Pulse Survey.";
          } else if (response.status === 404) {
            this.error =
              "You do not have the correct rights to delete this Pulse Survey";
          } else {
            this.error =
              "There was a server. We have notified our staff. Please try again later";
          }
        });
    },
    removeDimension(id) {
      this.dimensions = this.dimensions.filter(d => d.id !== id);
    }
  }
};
</script>

<style lang="less">
.pulse-survey {
  .d-header {
    text-transform: uppercase;
    font-size: 18px;
    letter-spacing: 1px;
  }
  .survey-question {
    font-size: 16px;
    letter-spacing: 1px;
    cursor: pointer;
    text-decoration: underline;
  }
  .section-headers {
    padding-bottom: 0px;
  }
  .small-bottom {
    margin-bottom: 30px;
  }
  .label {
    text-transform: uppercase;
    letter-spacing: 1px;
    padding-top: 10px;
    font-size: 14px;
    opacity: 0.7;
  }
  .action-btn {
    margin-top: 15px;
  }
}
</style>
